import React, { useCallback, useMemo, useState } from 'react';
import {
  addNotification,
  notificationStates,
} from '../../../components/Notification';
import { DEMO_IMPORT_LOGS } from '../EmployeeList.demoData';
import { IntegrationImportTabView } from './IntegrationImportTab.view';

// This tab is only ever rendered from the demo EmployeeList container (see
// EmployeeList.demo.component.jsx), so it always sources its log list from
// the static DEMO_IMPORT_LOGS fixture rather than subscribing to
// `companyDatabase/{companyId}/integrations/{source}/importLogs`, and
// "Re-import" is a no-op notification rather than calling the real
// per-integration import service.
export const IntegrationImportTabDemoComponent = ({
  companyId,
  teamId,
  source,
}) => {
  const [statusFilter, setStatusFilter] = useState('all');

  const sourceName =
    source === 'quickbooks'
      ? 'QuickBooks'
      : source === 'sagehr'
        ? 'Sage HR'
        : source === 'zohopeople'
          ? 'Zoho People'
          : 'Salesforce';

  // Demo mode: no Firestore subscription — read the latest fake import run
  // straight out of the static fixture (mirrors the real query's `.orderBy
  // ('importedAt', 'desc').limit(1)`, which is why `logs[0]` is "latest").
  const latestLog = DEMO_IMPORT_LOGS[source]?.logs?.[0] ?? null;
  const logMeta = latestLog
    ? {
        id: latestLog.id,
        importedAt: latestLog.importedAt
          ? new Date(latestLog.importedAt)
          : null,
        importedBy: latestLog.importedBy || {},
        status: latestLog.status || 'completed',
        stats: latestLog.stats || {},
      }
    : null;
  const employees = useMemo(() => latestLog?.employees || [], [latestLog]);

  const counts = useMemo(() => {
    const c = {
      all: employees.length,
      imported: 0,
      invited: 0,
      mapped: 0,
      skipped: 0,
      error: 0,
    };
    for (const e of employees) {
      if (c[e.outcome] !== undefined) c[e.outcome]++;
    }
    return c;
  }, [employees]);

  const filteredEmployees = useMemo(() => {
    if (statusFilter === 'all') return employees;
    return employees.filter((e) => e.outcome === statusFilter);
  }, [employees, statusFilter]);

  // Demo mode: no real integration to call out to — let the user know
  // instead of silently doing nothing (and instead of pretending a real
  // import ran).
  const handleReimport = useCallback(() => {
    addNotification({
      message: `Re-import from ${sourceName} is disabled in demo mode.`,
      level: notificationStates.info,
    });
  }, [sourceName]);

  return (
    <IntegrationImportTabView
      companyId={companyId}
      teamId={teamId}
      source={source}
      sourceName={sourceName}
      loading={false}
      logMeta={logMeta}
      counts={counts}
      statusFilter={statusFilter}
      onStatusFilterChange={setStatusFilter}
      filteredEmployees={filteredEmployees}
      reimporting={false}
      onReimport={handleReimport}
    />
  );
};
